<template lang="pug">
el-dialog(
  class="text-left-aligned"
  title="Pausar assinatura"
  :visible.sync="isVisible",
  width="720px"
)

  el-form(ref="form", :rules="rules", :model="data", label-width="200px")

    el-form-item(label="Retomar assinatura em", prop="resumeAt")
      el-date-picker.stretched(
        type="date",
        v-model="data.resumeAt",
        format="dd/MM/yyyy",
        value-format="yyyy-MM-ddT12:00:00.000Z"
        :clearable="false"
      )

    div(v-if="showAdvanced")
      el-form-item(label="Plano ao retormar", prop="planId")
        el-select.stretched(v-model="data.planId")
          el-option-group(key="current", label="Planos atuais")
            el-option(
              v-for="plan in availablePlans"
              :label="`${plan.name}${plan.planId == subscription.planId ? ' (plano atual)' : ''}`"
              :value="plan.planId"
            )
              span(style='float: left;') {{plan.name}}
              span(v-if="plan.planId == subscription.planId", style='font-weight: bold; float: right;') (plano atual)


          el-option-group(key="old", label="Planos antigos")
            el-option(
              v-for="plan in oldMigrationPlans"
              :label="`${plan.name}${plan.planId == subscription.planId ? ' (plano atual)' : ''}`"
              :value="plan.planId"
            )
              span(style='float: left;') {{plan.name}}
              span(v-if="plan.planId == subscription.planId", style='font-weight: bold; float: right;') (plano atual)

  div(style="text-align: right;")
    el-button(v-if="showAdvanced", type="text", size="mini", @click="toggleAdvanced") Ocultar opções avançadas
    el-button(v-else, type="text", size="mini", @click="toggleAdvanced") Opções avançadas

  p
    | Ao pausar esta assinatura, ela será
    b &#32;interrompida&#32;
    | , ficará com o status de 'Migrada' e uma
    b &#32;nova assinatura será iniciada no dia {{ fromSystemDate(data.resumeAt) }}
    span(v-if="planDescription")
      | &#32;com o&#32;
      b {{planDescription}}.
    span(v-else) .

  p
    b Aviso:&#32;
    | Antes de pausar esta assinatura, certifique-se de que a retomada da nova assinatura&#32;
    | ocorrerá após o fim dos períodos pagos pela assinatura atual.

  .footer(slot="footer")
    el-button(@click="close", :disabled="isPausing") Voltar

    el-button(type="primary", @click="promptPause", :loading="isPausing") Pausar assinatura

</template>
<script setup>
import { default as SubscriptionService, availablePlans, oldMigrationPlans } from '@/services/billing/SubscriptionService';
import { useMessage } from '@/utils/Message';
import { useConfirm } from '@/utils/MessageBox';
import { fromSystemDate } from '@/utils/DateTimeUtils';

import { computed, nextTick, ref } from 'vue';

const confirm = useConfirm()
const message = useMessage()

const props = defineProps({
  subscription: {
    type: Object,
    required: true
  },
  cancellationInfo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['paused'])

const form = ref()
const rules = ref({
  resumeAt: [
    { required: true, trigger: 'change', message: "Campo obrigatório" },
    {
      validator: (rule, value, callback) => {
        const now = new Date()
        const date = new Date(value)

        if (Math.floor(date.getTime() /(1000 * 60 * 60 * 24)) < Math.floor((now.getTime() / (1000 * 60 * 60 * 24)))) {
          callback(new Error("Deve ser uma data futura"))
        } else {
          callback()
        }
      },
      trigger: 'change'
    }
  ]
})

const isVisible = ref(false)
const isPausing = ref(false)
const showAdvanced = ref(false)

const data = ref({})

const planDescription = computed(() => {
  const plan = availablePlans.find((plan) => plan.planId == data.value.planId) ??
    oldMigrationPlans.find((plan) => plan.planId == data.value.planId) ?? null

  return plan?.name ?? null
})

const toggleAdvanced = () => {
  showAdvanced.value = !showAdvanced.value

  if (!showAdvanced.value) {
    data.value.planId = null
  }
}

const open = () => {
  const resumeAt =
    props.cancellationInfo.nextRenewalAt ? new Date(`${props.cancellationInfo.nextRenewalAt}T12:00:00.000Z`) : new Date()

  data.value = {
    planId: null,
    resumeAt: resumeAt
  }

  isVisible.value = true
  showAdvanced.value = false

  nextTick(() => {
    form.value.clearValidate()
  })
}

const close = () => {
  isVisible.value = false
}

const promptPause = async () => {
  var isValid = false

  try {
    await form.value.validate()

    isValid = true
  } catch(e) {
    message.warning("Verifique os dados preenchidos")

    return
  }

  confirm("Você tem certeza de que deseja pausar esta assinatura? Esta operação não pode ser desfeita", "Pausar assinatura", {
    confirmButtonText: 'Sim, pausar assinatura',
    cancelButtonText: 'Cancelar',
    type: 'warning'
  }).then(() => {
    return pauseSubscription()
  })
}

const pauseSubscription = async () => {
  isPausing.value = true

  try {
    const newSubscription = await SubscriptionService.pause(props.subscription.id, {
      planId: data.value.planId,
      resumeAt: data.value.resumeAt
    })

    emit('paused', newSubscription)

    message.success(`Assinatura pausada com sucesso!`)

    return newSubscription
  } catch(e) {
    message.error(`Houve um problema ao pausar assinatura: ${e}`)

    console.error(e)
  } finally {
    isPausing.value = false

    close()
  }
}

defineExpose({
  open
})
</script>
<style lang="sass" scoped>
p
  word-break: break-word

.el-select
  width: 320px
</style>
